import {useCallback, useContext, useEffect, useState} from 'react';

import {MessageContext} from 'src/contexts/message-context';
import {getEnabledCrmModules, saveCrmModules} from 'src/openapi/admin/apis/CrmModuleSettingsControllerApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import type {CrmType} from 'src/openapi/admin/models/CrmType';
import type {Error} from 'src/openapi/admin/models/Error';

type Props = {
    licenseId: number,
    crmType: CrmType,
};

export const useEnabledCrmModules = ({licenseId, crmType}: Props) => {
    const apiDispatch = useAppDispatch();
    const {setErrorMessage} = useContext(MessageContext);

    const [isLoading, setIsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<Error | null>(null);
    const [enabled, setEnabled] = useState<boolean>(false);

    useEffect(() => {
        const loadCrmModules = async () => {
            setIsLoading(true);
            const response = await apiDispatch(getEnabledCrmModules({licenseId, crmType}));
            if (response.payload.success) {
                setEnabled(response.payload.data);
                setLoadingError(null);
            } else {
                setLoadingError(response.payload.data);
            }
            setIsLoading(false);
        };

        loadCrmModules();
    }, [licenseId, crmType, apiDispatch]);

    const saveCrmModuleState = useCallback(async (value: boolean) => {
        await apiDispatch(saveCrmModules({licenseId, crmType, booleanJson: {value}})).then(response => {
            if (response.payload.error) {
                setErrorMessage(getError({
                    error: response.payload.data,
                    separator: '\n',
                }));
                setEnabled(false);
            } else if (response.payload.success) {
                setEnabled(value);
            }
        });
    }, [licenseId, apiDispatch, crmType, setErrorMessage]);

    return {
        isLoading,
        loadingError,
        enabled,
        saveCrmModuleState,
    };
};
